import { Droppable, Draggable } from '@hello-pangea/dnd';
import { Target, Sparkles } from 'lucide-react';
import { Task, Tag } from '@/types/task';
import { TaskItem } from '@/components/TaskItem';
import { cn } from '@/lib/utils';

interface MainTaskZoneProps {
  task: Task | null;
  tags: Tag[];
  onToggle: (id: string) => void;
  onDelete: (id: string) => void;
  onUpdateTask?: (id: string, updates: Partial<Task>) => void;
  onCreateTag?: (name: string, color: string) => Promise<Tag | null>;
  isDraggingOver?: boolean;
  isDropDisabled?: boolean;
}

export function MainTaskZone({ task, tags, onToggle, onDelete, onUpdateTask, onCreateTag, isDraggingOver, isDropDisabled }: MainTaskZoneProps) {
  return (
    <Droppable droppableId="main-task" isDropDisabled={isDropDisabled}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.droppableProps}
          data-tour-target="main-task-zone"
          className={cn(
            "rounded-xl border-2 border-dashed transition-all duration-300 min-h-[100px]",
            snapshot.isDraggingOver || isDraggingOver
              ? "border-primary bg-primary/5 shadow-lg shadow-primary/10"
              : task
                ? "border-primary/30 bg-gradient-to-br from-primary/5 to-primary/10"
                : "border-muted-foreground/20 bg-muted/30"
          )}
        >
          <div className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <Target className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium text-primary">The ONE Thing</span>
            </div>
            
            {task ? (
              <Draggable key={task.id} draggableId={task.id} index={0}>
                {(dragProvided, dragSnapshot) => {
                  // During active drag, disable transition so item follows cursor precisely
                  // During drop animation, let the library's transition handle it
                  // When not dragging, add a smooth transition for position changes
                  const getTransitionStyle = () => {
                    if (dragSnapshot.isDragging && !dragSnapshot.isDropAnimating) {
                      return 'none';
                    }
                    if (dragSnapshot.isDropAnimating) {
                      return undefined;
                    }
                    return 'transform 0.2s cubic-bezier(0.2, 0, 0, 1)';
                  };
                  return (
                  <div
                    ref={dragProvided.innerRef}
                    {...dragProvided.draggableProps}
                    style={{
                      ...dragProvided.draggableProps.style,
                      transition: getTransitionStyle(),
                    }}
                    className={cn(
                      "relative",
                      dragSnapshot.isDragging && "z-50"
                    )}
                  >
                    <TaskItem
                      task={task}
                      tags={tags}
                      onToggle={onToggle}
                      onDelete={onDelete}
                      onUpdateTask={onUpdateTask}
                      onCreateTag={onCreateTag}
                      isDragging={dragSnapshot.isDragging}
                      dragHandleProps={dragProvided.dragHandleProps || undefined}
                      isMainTask
                    />
                  </div>
                );}}
              </Draggable>
            ) : (
              <div className="flex flex-col items-center justify-center py-4 text-center">
                <Sparkles className="h-8 w-8 text-muted-foreground/40 mb-2" />
                <p className="text-sm text-muted-foreground">
                  Drag your most important task here
                </p>
                <p className="text-xs text-muted-foreground/60 mt-1">
                  "If I could only get ONE thing done today..."
                </p>
              </div>
            )}
          </div>
          {provided.placeholder}
        </div>
      )}
    </Droppable>
  );
}
